/**
 * The route tree, built from the module registry rather than hand-written.
 *
 * 42 screens hand-written as 42 route files is 42 chances for the nav, the route and the scope
 * gate to disagree. Generating the tree from `MODULES` makes that class of drift impossible: one
 * row is one nav item, one path and one gate.
 *
 * Search params (`scope`, `theme`, `next`) are declared on the root and validated, so a hand-edited
 * URL with `?scope=root` falls back to a sane value instead of rendering an undefined-shaped
 * console.
 *
 * **The guard lives here, on the route, not inside the screens.** A screen that checks for a
 * session on mount has already rendered once, and `docs/05-admin/02` §5.2 asks for the opposite:
 * direct-URL access to a place you cannot be never white-screens and never flashes. `beforeLoad`
 * runs before the component exists, so a signed-out visit to `/money/payouts` is a redirect, not a
 * render — and `?next=` carries where they were going, because deep links surviving login is the
 * difference between a console you can share a link into and one you cannot (§2.1).
 */
import { useState } from 'react';
import { createRootRoute, createRoute, createRouter, redirect, type RouterHistory } from '@tanstack/react-router';
import { Shell, type ShellSearch } from './Shell';
import { parseWindowSearch } from './urlState';
import { QueryClientProvider } from '@tanstack/react-query';
import { ToastProvider } from './toasts';
import { createQueryClient } from '../lib/api/queryClient';
import { MODULES } from './modules';
import { SCREENS } from './screens';
import { authState } from '../lib/auth/session';
import { EmptyState } from '../components/feedback';
import { Button } from '../components/primitives';
import { resolveTheme } from '../theme/preference';

/** Where sign-in sends you when nothing asked for anywhere else. */
export const LOGIN_PATH = '/login';

export function validateShellSearch(search: Record<string, unknown>): ShellSearch {
  /* A URL theme wins; without one, this browser's last toggle, else the OS scheme (doc 03 §4). */
  const theme = resolveTheme(search.theme);
  const next = typeof search.next === 'string' && search.next.startsWith('/') ? search.next : undefined;
  /* An invitation token, carried in the link a superadmin sent. Never typed, never guessed. */
  const token = typeof search.token === 'string' && search.token.length > 0 ? search.token : undefined;
  /* No `scope` here any more. The acting scope is whatever `GET /v1/admin/me` granted, so it is
     derived in the shell rather than carried in the URL — a `?scope=` on a link advertised a
     switch, and `can()` was always UX shaping over a boundary the server enforces. */
  /* The analytics view state (doc 05 §2), kept only when it is valid — see urlState.ts. */
  return { theme, next, token, ...parseWindowSearch(search) };
}

export function NotFound() {
  return (
    <EmptyState
      icon="signpost"
      title="No such screen"
      body="That address does not belong to any console module. The nav on the left is the complete map."
      action={
        <a href="/">
          <Button size="sm" variant="secondary">
            Back to the dashboard
          </Button>
        </a>
      }
    />
  );
}

/** The toast host sits above the shell so any screen can post a receipt without owning a stack. */
function Root() {
  /* One cache per mount, not per module. In the browser that is the same thing — the app mounts
     once. In a test file it is the difference between independent cases and a cache that carries
     one test's answers into the next, which is exactly the kind of coupling that makes a suite
     pass in order and fail alone. */
  const [queryClient] = useState(createQueryClient);
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <Shell />
      </ToastProvider>
    </QueryClientProvider>
  );
}

export const rootRoute = createRootRoute({
  validateSearch: validateShellSearch,
  component: Root,
  notFoundComponent: NotFound,
});

const moduleRoutes = MODULES.map((m) =>
  createRoute({
    getParentRoute: () => rootRoute,
    path: m.path,
    beforeLoad: ({ location }) => {
      const signedIn = authState().status === 'signed-in';
      if (m.bare) {
        /* Sign-in is the one route a signed-out visitor may have. Someone already signed in who
           lands on it has no business being shown a login form — send them to the console. */
        if (signedIn) throw redirect({ to: '/', search: validateShellSearch(location.search) });
        return;
      }
      if (signedIn) return;
      throw redirect({
        to: LOGIN_PATH,
        search: { ...validateShellSearch(location.search), next: location.pathname },
      });
    },
    /* Non-null: every module has a screen, and a test asserts it. */
    component: SCREENS[m.key]!,
  }),
);

export const routeTree = rootRoute.addChildren(moduleRoutes);

/** Omit `history` in the app — the router then drives the real browser URL. Tests pass memory. */
export function createAdminRouter(history?: RouterHistory) {
  return createRouter({ routeTree, history, defaultNotFoundComponent: NotFound });
}
